import {
  createTableHighlightsRenderingStrategy,
  DomTableGeometryResolver,
  HighlightsController,
  VirtualGeometryResolver,
} from '@databases/highlights';
import {
  FIXED_HIGHLIGHTS_SURFACE_Z_INDEX,
  getTableBaseFragmentZIndex,
} from '@databases/selections';
import '@databases/highlights/styles';
import { container } from '@databases/service-container';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { createTableHighlightsLayoutAdapter } from './create-table-highlights-layout-adapter.ts';

const SCROLLABLE_INNER_CONTAINER_ID = 'scrollable-inner-container';
const SELECTIONS_CONTAINER_ELEMENT_ID = 'selections-overlay-container';

/**
 * Registers the table highlight composition root.
 *
 * It combines Table V1 DOM geometry, virtualization fallback, pinned-boundary
 * observation, and logical-to-physical projection behind one
 * `HighlightsController`. Feature consumers resolve only that controller and
 * create logical layers; bootstrap owns the table-specific strategy and its
 * lifecycle.
 */
export function initializeTableHighlightsController() {
  function setup() {
    container.registerSingleton('TableHighlightsController', () => {
      const baseResolver = new DomTableGeometryResolver(() =>
        document.getElementById(SCROLLABLE_INNER_CONTAINER_ID),
      );
      const resolver = new VirtualGeometryResolver(baseResolver);
      const layout = createTableHighlightsLayoutAdapter(() =>
        resolver.getScrollContainer(),
      );
      // https://switcheroo.atlassian.com/ui/gates/03224aff-4ea6-4b69-b340-24b388b98dd8/key/databases-frontend-highlight-scroll-reliability
      const enableNullBoundsRetry = getExperimentValue(
        'databasesFrontendHighlightScrollReliability',
        'enabled',
        { fireExperimentExposure: false },
      );

      return new HighlightsController(resolver, {
        id: SELECTIONS_CONTAINER_ELEMENT_ID,
        invalidateGeometry: () => resolver.clearCache(),
        createRenderingStrategy: (context) =>
          createTableHighlightsRenderingStrategy({
            ...context,
            layout,
            pinnedLeadingSurfaceZIndex: FIXED_HIGHLIGHTS_SURFACE_Z_INDEX,
            getBaseFragmentZIndex: getTableBaseFragmentZIndex,
            enableNullBoundsRetry,
          }),
      });
    });
  }

  function dispose() {
    const controller = container.resolve('TableHighlightsController');

    controller.dispose();
  }

  return {
    setup,
    dispose,
  };
}
